import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'
import type { ActualTimeEntry, ConsumableView, ControllingMatrix } from '@symphenia/shared-types'

/** AP-35 Phase I — Nachkalkulation, Arbeitszeiten, Verbrauchsmaterial (Detailblatt 8, Blatt 7 4.). */

export async function fetchControlling(projectId: string, until?: string, positionId?: string): Promise<ControllingMatrix> {
  return (await api.get(`/projects/${projectId}/controlling`, { params: { until, positionId } })).data.data
}

export interface ControllingSnapshot {
  id: string
  trigger: 'COMPLETED' | 'ARCHIVED' | 'MANUAL'
  takenAt: string
  until: string
  matrix: ControllingMatrix
}

export async function fetchSnapshots(projectId: string): Promise<ControllingSnapshot[]> {
  return (await api.get(`/projects/${projectId}/controlling/snapshots`)).data.data
}

export async function takeSnapshot(projectId: string): Promise<{ id: string; replaced: boolean }> {
  return (await api.post(`/projects/${projectId}/controlling/snapshots`)).data.data
}

export interface ActualEntriesFilter {
  from?: string
  until?: string
  userId?: string
  phaseId?: string
  stepId?: string
  onlyUnchecked?: boolean
  onlyUnbilled?: boolean
  page?: number
  limit?: number
}

export interface ActualEntriesResult {
  data: ActualTimeEntry[]
  meta: { total: number; page: number; limit: number; totalPages: number }
  totals: { hours: number; uncheckedHours: number; costAmount?: number; checkedOnly: boolean }
}

/** Phase K (I-12) — Export der Arbeitszeiten (Mailweg `EXPORT_PROJECT_ACTUALS`). */
export function actualEntriesExportRequest(
  projectId: string,
  positionId: string | null,
  params: ActualEntriesFilter & { format: string; orientation?: string },
  columns: { key: string; label: string }[],
): ExportRequest {
  const url = positionId ? `/projects/${projectId}/positions/${positionId}/actuals/entries/export` : `/projects/${projectId}/actuals/entries/export`
  return { url, params: { ...params, page: undefined, limit: undefined, columns: JSON.stringify(columns) } }
}

export async function fetchActualEntries(projectId: string, positionId: string | null, filter: ActualEntriesFilter): Promise<ActualEntriesResult> {
  const pfad = positionId ? `/projects/${projectId}/positions/${positionId}/actuals/entries` : `/projects/${projectId}/actuals/entries`
  return (await api.get(pfad, { params: filter })).data
}

export interface ConsumableInput {
  articleId?: string | null
  name?: string
  quantity?: number
  unitId?: string | null
  unitPrice?: number | null
  priceUnit?: number
  discountPercent?: number | null
  deliveredAt?: string
  note?: string | null
}

export async function fetchConsumables(projectId: string, positionId: string): Promise<ConsumableView[]> {
  return (await api.get(`/projects/${projectId}/positions/${positionId}/consumables`)).data.data
}

export async function createConsumable(projectId: string, positionId: string, dto: ConsumableInput & { name: string; quantity: number }): Promise<ConsumableView> {
  return (await api.post(`/projects/${projectId}/positions/${positionId}/consumables`, dto)).data.data
}

/** Löschen legt die Zeile in den Papierkorb (Nachtrag 8) — die Antwort nennt den Eintrag fürs Rückgängig. */
export async function deleteConsumable(id: string): Promise<{ trashId: string; name: string }> {
  return (await api.delete(`/projects/consumables/${id}`)).data.data
}

/** Neue Reihenfolge des Verbrauchs (Nachtrag 8); die Antwort ist die ganze Liste. */
export async function reorderConsumables(projectId: string, positionId: string, lineIds: string[]): Promise<ConsumableView[]> {
  return (await api.post(`/projects/${projectId}/positions/${positionId}/consumables/reorder`, { lineIds })).data.data
}

export interface ConsumableTrashEntry {
  id: string
  lineId: string
  name: string
  deletedAt: string
  deletedByName: string | null
  expiresAt: string
}

export async function fetchConsumableTrash(projectId: string, positionId: string): Promise<ConsumableTrashEntry[]> {
  return (await api.get(`/projects/${projectId}/positions/${positionId}/consumables/trash`)).data.data
}

export async function restoreConsumable(trashId: string): Promise<ConsumableView[]> {
  return (await api.post(`/projects/consumables/trash/${trashId}/restore`)).data.data
}
